import api from './api';

/**
 * App-wide analytics client. Batches page views, session + engagement-time samples,
 * and key conversions to POST /api/analytics/track. Privacy-light: a stable anonymous
 * id (so we can count unique visitors incl. logged-out) + a per-visit session id that
 * resets after 30 min of inactivity. Engagement = active (visible) time per path.
 */

const AID_KEY = 'pr.aid';
const SID_KEY = 'pr.sid';
const SID_TS_KEY = 'pr.sid.ts';
const SESSION_GAP_MS = 30 * 60 * 1000;
const LOCATION_KEY = 'pr.userLocation';
const FIRST_TOUCH_KEY = 'pr.ft';   // legacy string, rides every page view as `ref`
const FIRST_DOC_KEY = 'pr.ft1';    // the full first-touch document

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxxyxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function anonymousId() {
  let id = localStorage.getItem(AID_KEY);
  if (!id) { id = uuid(); localStorage.setItem(AID_KEY, id); }
  return id;
}

function sessionId() {
  const now = Date.now();
  const last = parseInt(sessionStorage.getItem(SID_TS_KEY) || '0', 10);
  let sid = sessionStorage.getItem(SID_KEY);
  if (!sid || now - last > SESSION_GAP_MS) {
    sid = uuid();
    sessionStorage.setItem(SID_KEY, sid);
  }
  sessionStorage.setItem(SID_TS_KEY, String(now));
  return sid;
}

function device() {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua) || window.innerWidth < 768) return 'mobile';
  return 'desktop';
}

function geo() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    if (!raw) return {};
    const loc = JSON.parse(raw);
    const city = (loc.label || '').split(',')[0].trim() || undefined;
    return { city, lat: loc.lat, lng: loc.lng };
  } catch { return {}; }
}

/**
 * Where this browser first arrived — captured ONCE, on the very first page view, and kept.
 *
 * Two keys on purpose. `pr.ft` is the original string ('' | host | 'utm:<source>') that
 * rides every page view as `ref`; its rule is unchanged because the admin panel's referrer
 * breakdowns read it out of existing rows. `pr.ft1` is the full document — landing path,
 * referrer AND the campaign, rather than the referrer being thrown away whenever a link
 * named a utm_source — and it is what a sign-up sends so the account keeps its own origin.
 *
 * A browser that was already here before `pr.ft1` existed gets NO document, deliberately:
 * its first page view is long gone, and recording today's path as the landing path would
 * be a fabrication. Those accounts read WE DON'T KNOW, which is true.
 */
function ensureFirstTouch() {
  try {
    const raw = localStorage.getItem(FIRST_DOC_KEY);
    if (raw) return JSON.parse(raw);
    // The legacy key already being there means we missed this browser's first touch.
    if (localStorage.getItem(FIRST_TOUCH_KEY) != null) return null;

    const q = new URLSearchParams(window.location.search);
    const clip = (v, n) => (v ? String(v).trim().slice(0, n) : '');
    let referrer = '';
    if (document.referrer) {
      try {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) referrer = host.slice(0, 120);
      } catch { /* a referrer we cannot parse is a direct visit */ }
    }
    const doc = {
      path: (window.location.pathname || '/').slice(0, 200),
      referrer,
      utmSource: clip(q.get('utm_source'), 60),
      utmMedium: clip(q.get('utm_medium'), 60),
      utmCampaign: clip(q.get('utm_campaign'), 100),
      utmTerm: clip(q.get('utm_term'), 60),
      utmContent: clip(q.get('utm_content'), 60),
      at: new Date().toISOString(),
    };
    localStorage.setItem(FIRST_DOC_KEY, JSON.stringify(doc));
    return doc;
  } catch { return null; }
}

/**
 * The first-touch string that rides every page view as `ref`: a bare host
 * ("instagram.com") or "utm:<source>" when a campaign named itself; an empty string means
 * "we looked and it was direct", which is why it is stored rather than re-derived.
 */
function firstTouch() {
  try {
    const saved = localStorage.getItem(FIRST_TOUCH_KEY);
    if (saved != null) return saved;
    const doc = ensureFirstTouch();
    const value = doc ? (doc.utmSource ? `utm:${doc.utmSource}` : doc.referrer) : '';
    localStorage.setItem(FIRST_TOUCH_KEY, value);
    return value;
  } catch { return ''; }
}

/**
 * What a sign-up sends so the new account keeps where it came from. Null when this
 * browser's first touch was never captured — the server then leaves those columns alone.
 */
export function firstTouchPayload() {
  const doc = ensureFirstTouch();
  if (!doc) return undefined;
  const out = {
    path: doc.path || undefined,
    referrer: doc.referrer ?? undefined,
    utmSource: doc.utmSource || undefined,
    utmMedium: doc.utmMedium || undefined,
    utmCampaign: doc.utmCampaign || undefined,
  };
  return Object.values(out).some((v) => v !== undefined) ? out : undefined;
}

let queue = [];
let started = false;
let currentPath = null;
let activeStart = 0;       // when the visible clock started
let accumulatedMs = 0;     // active ms on the current path so far

function base(type, props) {
  const g = geo();
  return {
    type,
    anonymousId: anonymousId(),
    sessionId: sessionId(),
    path: currentPath || window.location.pathname,
    city: g.city,
    lat: g.lat,
    lng: g.lng,
    device: device(),
    properties: props ? JSON.stringify(props) : undefined,
  };
}

function enqueue(evt) {
  queue.push(evt);
  if (queue.length >= 12) flush();
}

async function flush(useBeacon = false) {
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  const body = { events };
  try {
    if (useBeacon && navigator.sendBeacon) {
      const url = `${api.defaults.baseURL}/analytics/track`;
      navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: 'application/json' }));
    } else {
      await api.post('/analytics/track', body);
    }
  } catch {
    // best-effort; drop on failure so we never block the UI
  }
}

// Fold the active (visible) time on the current path into an engagement sample.
function flushEngagement() {
  if (activeStart > 0) { accumulatedMs += Date.now() - activeStart; activeStart = 0; }
  if (accumulatedMs > 1000 && currentPath) {
    enqueue({ ...base('engagement'), durationMs: Math.round(accumulatedMs) });
  }
  accumulatedMs = 0;
}

/** Record a route change: close out engagement on the old path, open the new one. */
export function trackPageView(path) {
  if (path === currentPath) return;
  flushEngagement();
  currentPath = path;
  activeStart = document.visibilityState === 'visible' ? Date.now() : 0;
  const ref = firstTouch();
  const ft = ensureFirstTouch();
  // `ref` keeps its old meaning and shape; the campaign rides alongside it rather than
  // replacing it, which is the whole point of the document.
  const props = { ...(ref ? { ref } : {}), ...(ft?.utmCampaign ? { campaign: ft.utmCampaign } : {}), ...(ft?.utmMedium ? { medium: ft.utmMedium } : {}) };
  enqueue(base('page_view', Object.keys(props).length ? props : undefined));
}

/** Record an arbitrary product event (e.g. 'signal_created', 'application_sent', 'checkout_paid'). */
export function trackEvent(type, props) {
  if (!type) return;
  enqueue(base(type, props));
}

/** Wire timers + visibility/unload handlers. Safe to call once. */
export function initAnalytics() {
  if (started) return;
  started = true;
  sessionId(); // stamp a session immediately

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (activeStart > 0) { accumulatedMs += Date.now() - activeStart; activeStart = 0; }
      flush();
    } else {
      activeStart = Date.now();
    }
  });

  window.addEventListener('pagehide', () => { flushEngagement(); flush(true); });
  setInterval(() => flush(), 10000);

  // First view for the entry path.
  trackPageView(window.location.pathname);
}

const analytics = { initAnalytics, trackPageView, trackEvent, firstTouchPayload };
export default analytics;
